import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tag, Button, message, Spin, Input, Icon, Tooltip } from 'antd';
import { FixedSizeList } from 'react-window';
import moment from 'moment';

import Layout from '~/components/UI/Layout/Layout';
import { ModuleHeader } from '~/components/ModuleHeader';
import CustomerForm from '~/_domains/customers/drawer/index';

import { relatorioCampanha, baixarRelatorioPdf } from '../api';
import { statusLabel, statusColor, SOURCE_LABEL, explicaFunil, taxa } from '../helpers';
import ClienteLink from '../ClienteLink';
import VerEmail from './VerEmail';

const GRADE = '1.1fr 1.1fr 92px 118px 1fr';
const ALTURA_LINHA = 52;

const EVENTO = {
  delivered: { label: 'Entregue', cor: '#237804', fundo: '#f6ffed', borda: '#b7eb8f' },
  opened: { label: 'Aberto', cor: '#237804', fundo: '#f6ffed', borda: '#b7eb8f' },
  clicked: { label: 'Clicou', cor: '#237804', fundo: '#f6ffed', borda: '#b7eb8f' },
  pendent: { label: 'Saindo', cor: '#8c8c8c', fundo: '#fafafa', borda: '#e8e8e8' },
  // Rejeição do provedor: o Mailgun manda `failed`, o webhook do Resend grava
  // `bounced`. Sem estes dois a linha caía em "Enviado" enquanto o cartão
  // de cima já contava a falha.
  failed: { label: 'Rejeitado', cor: '#a8071a', fundo: '#fff1f0', borda: '#ffa39e' },
  bounced: { label: 'Rejeitado', cor: '#a8071a', fundo: '#fff1f0', borda: '#ffa39e' },
  bounce: { label: 'Rejeitado', cor: '#a8071a', fundo: '#fff1f0', borda: '#ffa39e' },
  blacklist: { label: 'Bloqueado', cor: '#a8071a', fundo: '#fff1f0', borda: '#ffa39e' },
};

const SITUACAO = {
  sent: { label: 'Enviado', cor: '#0958d9', fundo: '#e6f4ff', borda: '#91caff' },
  queued: { label: 'Na fila', cor: '#8c8c8c', fundo: '#fafafa', borda: '#e8e8e8' },
  pending: { label: 'Aguardando', cor: '#8c8c8c', fundo: '#fafafa', borda: '#e8e8e8' },
  failed: { label: 'Falhou', cor: '#a8071a', fundo: '#fff1f0', borda: '#ffa39e' },
  skipped: { label: 'Não enviado', cor: '#8c8c8c', fundo: '#fafafa', borda: '#e8e8e8' },
};

/** Etiqueta de estado — pílula própria, para poder colorir por semântica. */
const Pilula = ({ def }) => (
  <span style={{
    display: 'inline-block', fontSize: 11.5, fontWeight: 600, lineHeight: 1.6,
    padding: '1px 9px', borderRadius: 20,
    color: def.cor, background: def.fundo, border: '1px solid ' + def.borda,
    whiteSpace: 'nowrap',
  }}>
    {def.label}
  </span>
);

/**
 * Painel de números.
 *
 * Um bloco só, com o que saiu em destaque e o resto como leitura de apoio —
 * cinco caixas de peso igual obrigavam a comparar tudo com tudo para descobrir
 * o que importava.
 */
const Painel = ({ estatisticas, funil, campanha }) => {
  const enviados = estatisticas.enviados || 0;
  const naoEntregues = (estatisticas.falhas || 0) + (estatisticas.nao_enviados || 0);
  const semEventos = enviados > 0 && !estatisticas.entregues && !estatisticas.abertos;

  const secundario = (rotulo, valor, sobre, cor) => (
    <div style={{ flex: '1 1 0', minWidth: 108, padding: '0 18px', borderLeft: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: '#8c8c8c' }}>
        {rotulo}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, color: cor || '#1f2430', lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </div>
      <div style={{ fontSize: 12, color: '#8c8c8c' }}>{sobre}</div>
    </div>
  );

  return (
    <div style={{
      background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
      padding: '18px 20px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: '0 0 200px', paddingRight: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: '#8c8c8c' }}>
            Enviados
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.05, color: '#1f2430', fontVariantNumeric: 'tabular-nums' }}>
              {enviados}
            </span>
            {funil && (
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>de {funil.receberao} elegíveis</span>
            )}
          </div>
          <div style={{ height: 6, borderRadius: 3, background: '#f0f0f0', marginTop: 10, overflow: 'hidden' }}>
            <div style={{
              width: (funil && funil.receberao ? Math.round((enviados / funil.receberao) * 100) : 100) + '%',
              height: '100%', background: '#52c41a',
            }} />
          </div>
        </div>

        {secundario('Entregues', estatisticas.entregues, taxa(estatisticas.entregues, enviados), '#237804')}
        {secundario('Abertos', estatisticas.abertos, taxa(estatisticas.abertos, estatisticas.entregues), '#0958d9')}
        {secundario('Cliques', estatisticas.cliques, taxa(estatisticas.cliques, estatisticas.entregues), '#531dab')}
        {secundario(
          'Não entregues',
          naoEntregues,
          (estatisticas.falhas || 0) + ' falharam · ' + (estatisticas.nao_enviados || 0) + ' não saíram',
          naoEntregues ? '#cf1322' : '#8c8c8c'
        )}
      </div>

      {/* Zero por falta de dado e zero de verdade se parecem. Enquanto o
          gateway não reporta, a tela precisa dizer qual dos dois é. */}
      {semEventos && (
        <div style={{
          marginTop: 14, padding: '9px 12px', borderRadius: 8,
          background: '#fffbe6', border: '1px solid #ffe58f',
          fontSize: 12.5, color: '#874d00',
        }}>
          <Icon type="clock-circle" style={{ marginRight: 6 }} />
          Entrega e abertura chegam pelo retorno do provedor de e-mail, que costuma
          levar alguns minutos. Zero aqui ainda significa “sem resposta”, não “ninguém recebeu”.
          {campanha.sent_at && ' Disparo às ' + moment(campanha.sent_at).format('HH:mm') + '.'}
        </div>
      )}
    </div>
  );
};

const RelatorioCampanha = ({ match, history }) => {
  const id = match && match.params && match.params.id;
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [aba, setAba] = useState('todos');
  const [baixandoPdf, setBaixandoPdf] = useState(false);

  const carregar = useCallback(() => {
    relatorioCampanha(id)
      .then((r) => setDados(r))
      .catch(() => message.error('Não foi possível carregar o relatório.'))
      .finally(() => setCarregando(false));
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (!dados || !dados.campanha || dados.campanha.status !== 'sending') return;
    const timer = setInterval(carregar, 15000);
    return () => clearInterval(timer);
  }, [dados, carregar]);

  const baixarPdf = () => {
    setBaixandoPdf(true);
    baixarRelatorioPdf(id)
      .then((blob) => {
        if (!blob) return;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'campanha-' + id + '.pdf';
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => message.error('Não foi possível gerar o PDF.'))
      .finally(() => setBaixandoPdf(false));
  };

  const destinatarios = (dados && dados.destinatarios) || [];

  const abas = useMemo(() => ([
    { chave: 'todos', rotulo: 'Todos', total: destinatarios.length },
    { chave: 'sent', rotulo: 'Enviados', total: destinatarios.filter((d) => d.status === 'sent').length },
    { chave: 'skipped', rotulo: 'Não enviados', total: destinatarios.filter((d) => d.status === 'skipped').length },
    // `falhou` vem do backend: cobre o erro de envio e a rejeição do provedor.
    { chave: 'failed', rotulo: 'Falhas', total: destinatarios.filter((d) => d.falhou).length },
  ]), [destinatarios]);

  const filtrados = useMemo(() => {
    const alvo = busca.toLowerCase();
    return destinatarios.filter((d) => {
      if (aba === 'failed' && !d.falhou) return false;
      if (aba !== 'todos' && aba !== 'failed' && d.status !== aba) return false;
      if (!alvo) return true;
      return (d.email || '').toLowerCase().indexOf(alvo) >= 0
        || (d.cliente || '').toLowerCase().indexOf(alvo) >= 0;
    });
  }, [destinatarios, aba, busca]);

  /** Quem ficou de fora vira tarefa: a planilha é o formato em que o time age. */
  const baixarCsv = () => {
    const linhas = destinatarios
      .filter((d) => d.status === 'skipped' || d.falhou)
      .map((d) => [d.codigo || '', d.cliente || '', d.email || '', d.motivo || '']);

    if (!linhas.length) {
      message.info('Todo mundo do filtro recebeu — não há lista para baixar.');
      return;
    }

    const csv = String.fromCharCode(0xfeff) + [['Código', 'Cliente', 'E-mail', 'Motivo']]
      .concat(linhas)
      .map((l) => l.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(';'))
      .join('\n');

    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    link.download = 'campanha-' + id + '-nao-receberam.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (carregando) {
    return <Layout><div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div></Layout>;
  }
  if (!dados) return <Layout><div style={{ padding: 40 }}>Relatório indisponível.</div></Layout>;

  const { campanha, estatisticas, funil } = dados;

  const Linha = ({ index, style }) => {
    const d = filtrados[index];
    const evento = d.evento && EVENTO[d.evento];
    const def = d.status === 'sent' && evento ? evento : (SITUACAO[d.status] || SITUACAO.pending);

    return (
      <div style={{
        ...style, display: 'grid', gridTemplateColumns: GRADE, gap: 12,
        alignItems: 'center', padding: '0 18px',
        borderBottom: '1px solid #f5f5f5', background: '#fff', fontSize: 13,
      }}>
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {d.email || <span style={{ color: '#bfbfbf' }}>sem endereço</span>}
        </div>

        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {d.cliente ? (
            <ClienteLink id={d.cliente_id} nome={d.cliente} />
          ) : <span style={{ color: '#bfbfbf' }}>—</span>}
          {d.codigo && <span style={{ color: '#bfbfbf', marginLeft: 6 }}>#{d.codigo}</span>}
        </div>

        <div style={{ color: '#8c8c8c', fontSize: 12 }}>
          {d.origem ? (d.origem === 'painel' ? 'Painel' : 'Empresa') : '—'}
        </div>

        <div><Pilula def={def} /></div>

        <div style={{ color: '#8c8c8c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {d.motivo || ''}
        </div>
      </div>
    );
  };

  const alturaLista = Math.min(Math.max(filtrados.length, 1) * ALTURA_LINHA, 520);

  return (
    <Layout>
      {/* Nada de botão "Voltar": a migalha já é o caminho de volta e estava ali
          o tempo todo, só não clicável. Um botão a mais no cabeçalho competia
          com a única ação de verdade da tela, que é levar o relatório embora. */}
      <ModuleHeader
        breadcrumbs={[
          'Marketing',
          { label: 'Campanhas', onClick: () => history.push('/marketing/campanhas') },
          campanha.name,
        ]}
        title={campanha.name}
        actions={
          <span style={{ display: 'flex', gap: 8 }}>
            <VerEmail id={id} />
            <Button icon="file-pdf" style={{ borderRadius: 8 }} loading={baixandoPdf} onClick={baixarPdf}>
              PDF
            </Button>
          </span>
        }
      />

      <div style={{
        background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10,
        padding: '14px 18px', marginBottom: 14,
        display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 14, color: '#1f2430' }}>{campanha.subject}</div>
          <div style={{ marginTop: 6, fontSize: 13, color: '#8c8c8c' }}>
            <Tag color={statusColor(campanha.status)}>{statusLabel(campanha.status)}</Tag>
            {campanha.sent_at && 'enviada em ' + moment(campanha.sent_at).format('DD/MM/YYYY [às] HH:mm')}
            {' · ' + (SOURCE_LABEL[campanha.source] || '')}
          </div>
        </div>
        {funil && (
          <div style={{ fontSize: 12, color: '#8c8c8c', textAlign: 'right' }}>
            <div>{funil.clientes} cliente(s) no filtro</div>
            {explicaFunil(funil).length > 0 && <div>não receberam: {explicaFunil(funil).join(', ')}</div>}
          </div>
        )}
      </div>

      <Painel estatisticas={estatisticas} funil={funil} campanha={campanha} />

      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '10px 14px', borderBottom: '1px solid #e8e8e8',
        }}>
          {abas.map((a) => (
            <button
              key={a.chave}
              onClick={() => setAba(a.chave)}
              style={{
                border: '1px solid ' + (aba === a.chave ? '#1890ff' : '#e8e8e8'),
                background: aba === a.chave ? '#e6f4ff' : '#fff',
                color: aba === a.chave ? '#0958d9' : '#595959',
                fontWeight: aba === a.chave ? 600 : 400,
                borderRadius: 20, padding: '3px 13px', fontSize: 13, cursor: 'pointer',
                outline: 'none',
              }}
            >
              {a.rotulo} <span style={{ opacity: .65 }}>{a.total}</span>
            </button>
          ))}

          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Input
              size="small"
              prefix={<Icon type="search" />}
              placeholder="buscar por e-mail ou cliente"
              style={{ width: 230 }}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            <Tooltip title="Planilha com quem não recebeu e o motivo — para o time corrigir os cadastros">
              <Button size="small" icon="download" onClick={baixarCsv}>Quem não recebeu</Button>
            </Tooltip>
          </span>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: GRADE, gap: 12, padding: '9px 18px',
          background: '#fafafa', borderBottom: '1px solid #e8e8e8',
          fontSize: 11, fontWeight: 600, letterSpacing: '.05em',
          textTransform: 'uppercase', color: '#8c8c8c',
        }}>
          <div>Destinatário</div><div>Cliente</div><div>Origem</div><div>Situação</div><div>Motivo</div>
        </div>

        {filtrados.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
            Nada nesta aba.
          </div>
        ) : (
          <FixedSizeList
            height={alturaLista}
            itemCount={filtrados.length}
            itemSize={ALTURA_LINHA}
            width="100%"
          >
            {Linha}
          </FixedSizeList>
        )}
      </div>

      <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 10 }}>
        A lista inclui quem não recebeu e o motivo — “sem e-mail cadastrado” é tarefa
        para o time, não estatística perdida.
      </div>

      <CustomerForm />
    </Layout>
  );
};

export default RelatorioCampanha;
